import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../api";
import { Helmet } from "react-helmet-async";

const EditPassword = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    oldPassword: "",
    password: "",
    password_confirmation: "",
  });

  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (formData.password !== formData.password_confirmation) {
      setSubmitError("Les mots de passe ne correspondent pas.");
      return;
    }

    try {
      setLoading(true);

      await api.put(`/user/${id}/edit/password`, formData);

      toast.success("Mot de passe modifié avec succès !");

      setFormData({
        oldPassword: "",
        password: "",
        password_confirmation: "",
      });

      setTimeout(() => {
        navigate("/profile");
      }, 1200);
    } catch (err) {
      console.error(err);

      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(" ");
        setSubmitError(msgs);
      } else {
        setSubmitError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Erreur lors de la modification du mot de passe."
        );
      }

      toast.error("Erreur lors de la modification du mot de passe.");
    } finally {
      setLoading(false);
    }
  };

  const isDisabled =
    loading ||
    !formData.oldPassword.trim() ||
    !formData.password.trim() ||
    !formData.password_confirmation.trim();

  return (
    <>
    <Helmet>
      <title>Modifier mon mot de passe | LMFP Photography</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>


    <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
      <div className="mx-auto max-w-4xl">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
              Sécurité du compte
            </p>

            <h1 className="mb-3 text-3xl font-bold md:text-4xl">
              Modifier mon mot de passe
            </h1>

            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Mettez à jour votre mot de passe pour renforcer la sécurité de
              votre compte.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">
                Nouveau mot de passe
              </h2>

              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="oldPassword"
                    className="mb-2 block text-sm font-medium text-slate-200"
                  >
                    Ancien mot de passe
                  </label>

                  <input
                    type="password"
                    name="oldPassword"
                    id="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="mb-2 block text-sm font-medium text-slate-200"
                  >
                    Nouveau mot de passe
                  </label>

                  <input
                    type="password"
                    name="password"
                    id="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />

                  <p className="mt-2 text-sm text-slate-400">
                    Minimum 8 caractères.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="password_confirmation"
                    className="mb-2 block text-sm font-medium text-slate-200"
                  >
                    Confirmation du mot de passe
                  </label>

                  <input
                    type="password"
                    name="password_confirmation"
                    id="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {submitError && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {submitError}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={isDisabled}
                className="w-full rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {loading ? "Modification..." : "Modifier"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="w-full rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition hover:bg-slate-800 sm:w-auto"
              >
                Annuler
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
    </>
  );
};

export default EditPassword;